import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Users, Loader2, Calendar, Sparkles, ArrowRight, MessageSquare, Trash2, ChevronDown, ChevronUp,
    ShieldCheck,
} from "lucide-react";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { CandidateReviewPanel } from "@/components/team/CandidateReviewPanel";
import { Avatar } from "@/components/Avatar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const STAGES = [
    { key: "screened", label: "Screened", accent: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
    { key: "interview", label: "Interview", accent: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
    { key: "offer", label: "Offer", accent: "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/30" },
    { key: "hired", label: "Hired", accent: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
    { key: "passed", label: "Passed", accent: "bg-muted text-muted-foreground border-border" },
];

const Card = ({ row, onStage, onRemove, selected, onToggleSelect }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            data-testid={`pipeline-${row.id}`}
            className={`card-soft p-4 ${selected ? "ring-2 ring-emerald-500/50 border-emerald-500/40" : ""}`}
        >
            <div className="flex items-start gap-3">
                {row.candidate_id && (
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => onToggleSelect(row)}
                        className="mt-2 rounded border-border accent-emerald-500"
                        data-testid={`pipeline-select-${row.id}`}
                        aria-label={selected ? "Deselect candidate" : "Select candidate"}
                    />
                )}
                <Avatar
                    url={row.profile_photo_url || null}
                    name={row.candidate_name || ""}
                    email={row.candidate_email || ""}
                    userId={row.candidate_id || row.id || ""}
                    size={36}
                    testId={`pipeline-avatar-${row.id}`}
                />
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-sm text-foreground truncate">{row.candidate_name || "Candidate"}</h3>
                        {row.match_score != null && <Badge variant="outline" className="text-[10px] mono-score">{row.match_score}%</Badge>}
                        {row.id_verified && (
                            <span
                                data-testid={`pipeline-verified-${row.id}`}
                                title="Government-ID verified via Stripe Identity"
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                            >
                                <ShieldCheck size={10} /> Verified
                            </span>
                        )}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">{row.job_title} · {row.company}</div>
                </div>
            </div>
            <div className="mt-3 flex items-center gap-2">
                <Select value={row.stage} onValueChange={(v) => onStage(row, v)}>
                    <SelectTrigger data-testid={`pipeline-stage-${row.id}`} className="h-8 text-xs flex-1">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STAGES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <button onClick={() => onRemove(row)} data-testid={`pipeline-remove-${row.id}`} className="text-muted-foreground hover:text-destructive p-1" aria-label="Remove">
                    <Trash2 size={13} />
                </button>
            </div>
            <button
                onClick={() => setExpanded((v) => !v)}
                data-testid={`pipeline-expand-${row.id}`}
                className="mt-3 w-full flex items-center justify-center gap-1 text-[11px] font-semibold text-brand-green hover:underline"
            >
                {expanded ? <>Hide review <ChevronUp size={12} /></> : <>Team review <ChevronDown size={12} /></>}
            </button>
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className="mt-3 overflow-hidden"
                    >
                        <CandidateReviewPanel pipelineId={row.id} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

const CandidatePipeline = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    // iter 149.2 — bulk-select set of pipeline candidate_ids for the
    // Request-identity-verification action.
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/employer/pipeline");
            setRows(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const g = Object.fromEntries(STAGES.map((s) => [s.key, []]));
        for (const r of rows) {
            const k = STAGES.some((s) => s.key === r.stage) ? r.stage : "screened";
            g[k].push(r);
        }
        return g;
    }, [rows]);

    const setStage = async (row, stage) => {
        try {
            await api.patch(`/employer/pipeline/${row.id}`, { stage });
            setRows((xs) => xs.map((x) => x.id === row.id ? { ...x, stage } : x));
            toast.success(`Moved to ${stage}.`);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (row) => {
        if (!window.confirm(`Remove ${row.candidate_name} from your pipeline?`)) return;
        try {
            await api.delete(`/employer/pipeline/${row.id}`);
            setRows((xs) => xs.filter((x) => x.id !== row.id));
            setSelected((s) => {
                if (!row.candidate_id || !s.has(row.candidate_id)) return s;
                const next = new Set(s); next.delete(row.candidate_id); return next;
            });
            toast.success("Removed.");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const toggleSelect = (row) => {
        if (!row.candidate_id) return;
        setSelected((s) => {
            const next = new Set(s);
            if (next.has(row.candidate_id)) next.delete(row.candidate_id);
            else next.add(row.candidate_id);
            return next;
        });
    };

    const requestBulkIdVerification = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/employer/id-verification/bulk-request", {
                candidate_ids: ids,
            });
            const t = data.tally || {};
            const bits = [];
            if (t.created) bits.push(`${t.created} request${t.created === 1 ? "" : "s"} sent`);
            if (t.already_requested) bits.push(`${t.already_requested} already open`);
            if (t.already_verified) bits.push(`${t.already_verified} already verified`);
            if (t.no_access) bits.push(`${t.no_access} skipped (no access)`);
            toast.success(bits.length ? bits.join(" · ") : "No changes.");
            setSelected(new Set());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBulkBusy(false);
        }
    };

    if (loading) return <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>;

    return (
        <div data-testid="pipeline-page">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
                <div>
                    <p className="overline">Your funnel</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Candidate Pipeline</h1>
                    <p className="text-sm text-muted-foreground mt-1">{rows.length} candidate{rows.length === 1 ? "" : "s"} across all your roles.</p>
                </div>
                {selected.size > 0 && (
                    <button
                        onClick={requestBulkIdVerification}
                        disabled={bulkBusy}
                        className="btn-green text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        data-testid="pipeline-bulk-idv-btn"
                    >
                        {bulkBusy
                            ? <><Loader2 size={14} className="animate-spin" /> Sending {selected.size}…</>
                            : <><ShieldCheck size={14} /> Request ID verification ({selected.size})</>}
                    </button>
                )}
            </div>

            {rows.length === 0 ? (
                <div className="card-soft p-12 text-center">
                    <Users size={22} className="mx-auto text-emerald-500" />
                    <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">No candidates in pipeline yet.</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">Add candidates from Find Talent or from any AI Hiring Brief. Move them through the funnel — from Screened all the way to Hired.</p>
                    <Link to="/employer/find-talent" className="btn-green mt-6 inline-flex text-sm">
                        Find Talent <ArrowRight size={13} className="ml-2" />
                    </Link>
                </div>
            ) : (
                <div className="overflow-x-auto pb-4">
                    <div className="grid grid-flow-col auto-cols-[280px] gap-4 min-w-max">
                        {STAGES.map((s) => (
                            <div key={s.key} data-testid={`stage-${s.key}`} className="flex flex-col">
                                <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider self-start ${s.accent}`}>
                                    {s.label} <span className="rounded-full bg-white/60 dark:bg-black/40 px-1.5">{grouped[s.key].length}</span>
                                </div>
                                <div className="mt-3 space-y-3 min-h-[120px]">
                                    {grouped[s.key].map((r) => <Card key={r.id} row={r} onStage={setStage} onRemove={remove} selected={r.candidate_id ? selected.has(r.candidate_id) : false} onToggleSelect={toggleSelect} />)}
                                    {grouped[s.key].length === 0 && (
                                        <div className="rounded-2xl border-2 border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                                            Nothing here yet.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-8 card-soft p-4 flex items-start gap-3">
                <Sparkles size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                    Move candidates through your funnel. Interview scheduling + team collaboration launching soon.{" "}
                    <Link to="/inbox" className="text-brand-green hover:underline"><MessageSquare size={11} className="inline mr-0.5" /> Message them directly</Link> anytime.
                </p>
            </div>
        </div>
    );
};

export default CandidatePipeline;
